/**
 * The game controller — the app-layer state machine that sits between the UI
 * and a backend. It owns transient interaction state (what's selected, which
 * squares are legal targets, whether the handoff curtain is up) and translates
 * clicks into engine Actions. The backend stays the single source of game
 * truth; this layer is all about *intent*.
 *
 * It is perspective-aware so the same UI serves both modes:
 *   - local hotseat: `mySeat` is null (you drive both seats) and an optional
 *     pass-the-device curtain hides hands between turns;
 *   - online: `mySeat` is your fixed seat and you may only act on your turn.
 */
import { createMemo, createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import {
  abilityDef,
  abilityDestinations,
  abilityRevives,
  abilitySources,
  captainMove as captainMoveAction,
  captainMovedThisTurn,
  discard as discardAction,
  legalDeploySquaresForPiece,
  legalMovesFrom,
  move as moveAction,
  mustDiscard,
  otherPlayer,
  pass as passAction,
  resign as resignAction,
  offerDraw as offerDrawAction,
  acceptDraw as acceptDrawAction,
  declineDraw as declineDrawAction,
  deploy as deployAction,
  playCard as playCardAction,
  type AbilityId,
  type Card,
  type Move,
  type PieceTypeId,
  type PlayerId,
  type Square,
} from "../core";
import { DEFAULT_SETTINGS, type SessionSettings } from "./config";
import type { GameBackend } from "./store";

export type Selection =
  | { readonly kind: "piece"; readonly square: Square }
  | { readonly kind: "card"; readonly cardId: string; readonly piece: PieceTypeId }
  // A relocation ability mid-cast: `from` is null until the source piece is
  // picked, then holds it while the player chooses a destination.
  | {
      readonly kind: "ability";
      readonly cardId: string;
      readonly ability: AbilityId;
      readonly from: Square | null;
    }
  // A revive ability awaiting a graveyard pick (resolved via the revive picker,
  // not a board click).
  | { readonly kind: "revive"; readonly cardId: string; readonly ability: AbilityId }
  | null;

export interface ControllerOptions {
  /** Fixed seat in online play; null for local hotseat (control both). */
  readonly mySeat?: PlayerId | null;
  /** Use the pass-the-device curtain (local only). */
  readonly useCurtain?: boolean;
  /** Read-only mode (a spectator or replay viewer): never allowed to act. */
  readonly spectator?: boolean;
  readonly settings?: SessionSettings;
}

export interface GameController {
  readonly store: GameBackend;
  readonly selection: () => Selection;
  readonly targets: () => Map<Square, { capture: boolean }>;
  readonly curtainUp: () => boolean;
  readonly settings: SessionSettings;
  /** The seat whose hand should be shown. */
  readonly viewer: () => PlayerId;
  /** Whether the local user may act right now. */
  readonly canAct: () => boolean;
  readonly mySeat: PlayerId | null;
  /** Graveyard piece types a selected Resurrection can return, else empty. */
  readonly reviveOptions: () => PieceTypeId[];
  /** True when the viewer's hand is over the limit and must be discarded down. */
  readonly mustDiscard: () => boolean;

  selectSquare(square: Square): void;
  selectCard(card: Card): void;
  /** Resolve a selected revive ability by choosing a graveyard piece type. */
  playRevive(type: PieceTypeId): void;
  clearSelection(): void;
  pass(): void;
  /** Concede the game from the local player's perspective. */
  resign(): void;
  /** The seat with a standing draw offer, or null. */
  readonly drawOffer: () => PlayerId | null;
  /** Offer the opponent a draw (from the local player's perspective). */
  offerDraw(): void;
  /** Accept the opponent's standing draw offer. */
  acceptDraw(): void;
  /** Decline the opponent's standing draw offer. */
  declineDraw(): void;
  undo(): void;
  reveal(): void;
  setHideHands(value: boolean): void;
}

export function createGameController(
  backend: GameBackend,
  options: ControllerOptions = {},
): GameController {
  const mySeat = options.mySeat ?? null;
  const useCurtain = options.useCurtain ?? false;

  const [selection, setSelection] = createSignal<Selection>(null);
  const [curtainUp, setCurtainUp] = createSignal(false);
  const [settings, setSettings] = createStore<{ hideHandsBetweenTurns: boolean }>({
    ...(options.settings ?? DEFAULT_SETTINGS),
  });

  // In online play the viewer is your fixed seat; locally it's the side to move.
  const viewer = (): PlayerId => mySeat ?? backend.state.turn;

  const canAct = (): boolean => {
    if (options.spectator) return false;
    if (backend.state.phase !== "playing") return false;
    if (curtainUp()) return false;
    return mySeat === null || backend.state.turn === mySeat;
  };

  const targets = createMemo<Map<Square, { capture: boolean }>>(() => {
    const sel = selection();
    const result = new Map<Square, { capture: boolean }>();
    if (!sel) return result;
    const turn = backend.state.turn;

    if (sel.kind === "piece") {
      for (const m of legalMovesFrom(backend.state, sel.square) as Move[]) {
        result.set(m.to, { capture: m.capture });
      }
    } else if (sel.kind === "card") {
      for (const sq of legalDeploySquaresForPiece(backend.state, turn, sel.piece)) {
        result.set(sq, { capture: false });
      }
    } else if (sel.kind === "ability") {
      if (sel.from === null) {
        // Step 1: ring the pieces this card can target.
        for (const sq of abilitySources(backend.state, turn, sel.ability)) {
          result.set(sq, { capture: true });
        }
      } else {
        // Step 2: dot the empty squares the chosen piece can be sent to.
        for (const sq of abilityDestinations(backend.state, turn, sel.ability, sel.from)) {
          result.set(sq, { capture: false });
        }
      }
    }
    return result;
  });

  const reviveOptions = (): PieceTypeId[] => {
    const sel = selection();
    if (sel?.kind !== "revive") return [];
    return abilityRevives(backend.state, backend.state.turn);
  };

  /** Apply an action and, on success, reset selection and raise the curtain. */
  function act(action: Parameters<GameBackend["dispatch"]>[0]): void {
    const ok = backend.dispatch(action);
    if (!ok) return;
    setSelection(null);
    if (useCurtain && settings.hideHandsBetweenTurns && backend.state.phase === "playing") {
      setCurtainUp(true);
    }
  }

  return {
    store: backend,
    selection,
    targets,
    curtainUp,
    settings,
    viewer,
    canAct,
    mySeat,
    reviveOptions,
    mustDiscard: () => mustDiscard(backend.state, backend.state.turn),

    selectSquare(square: Square) {
      if (!canAct()) return;
      const sel = selection();
      const turn = backend.state.turn;

      // Mid-cast relocation/recall/destroy ability: pick a source, then (for
      // relocate) a destination. Recall and Demolish have no destination — they
      // resolve immediately on the source pick.
      if (sel?.kind === "ability") {
        const t = abilityDef(sel.ability).target;
        const singleTarget = t === "recall" || t === "destroy";
        if (sel.from === null) {
          if (abilitySources(backend.state, turn, sel.ability).includes(square)) {
            if (singleTarget) act(playCardAction(turn, sel.cardId, { from: square }));
            else setSelection({ ...sel, from: square });
          }
          return;
        }
        if (targets().has(square)) {
          act(playCardAction(turn, sel.cardId, { from: sel.from, to: square }));
        } else if (abilitySources(backend.state, turn, sel.ability).includes(square)) {
          // Re-pick a different source piece.
          setSelection({ ...sel, from: square });
        } else {
          setSelection(null);
        }
        return;
      }

      const target = targets().get(square);
      if (sel && target) {
        if (sel.kind === "card") {
          act(deployAction(turn, sel.cardId, square));
        } else if (sel.kind === "piece") {
          const piece = backend.state.board[sel.square];
          // Moving the Captain spends its free move (doesn't end the turn) until
          // that's used; after that it moves like any piece.
          if (piece?.type === "captain" && !captainMovedThisTurn(backend.state)) {
            if (backend.dispatch(captainMoveAction(turn, sel.square, square))) setSelection(null);
          } else {
            act(moveAction(turn, sel.square, square));
          }
        }
        return;
      }

      const piece = backend.state.board[square];
      if (piece && piece.owner === turn) {
        setSelection({ kind: "piece", square });
      } else {
        setSelection(null);
      }
    },

    selectCard(card: Card) {
      if (!canAct()) return;
      // Over the hand limit: a card click discards it (no turn change, so no
      // curtain) rather than selecting it to play.
      if (mustDiscard(backend.state, backend.state.turn)) {
        if (backend.dispatch(discardAction(backend.state.turn, card.id))) setSelection(null);
        return;
      }
      const sel = selection();
      const sameCard =
        sel != null && "cardId" in sel && sel.cardId === card.id;
      if (sameCard) {
        setSelection(null);
        return;
      }
      if (card.kind === "piece") {
        setSelection({ kind: "card", cardId: card.id, piece: card.piece });
        return;
      }
      const def = abilityDef(card.ability);
      if (def.target === "revive") {
        setSelection({ kind: "revive", cardId: card.id, ability: card.ability });
      } else {
        setSelection({ kind: "ability", cardId: card.id, ability: card.ability, from: null });
      }
    },

    playRevive(type: PieceTypeId) {
      if (!canAct()) return;
      const sel = selection();
      if (sel?.kind !== "revive") return;
      act(playCardAction(backend.state.turn, sel.cardId, { reviveType: type }));
    },

    clearSelection() {
      setSelection(null);
    },

    pass() {
      if (!canAct()) return;
      act(passAction(backend.state.turn));
    },

    resign() {
      if (backend.state.phase !== "playing") return;
      backend.dispatch(resignAction(viewer()));
      setSelection(null);
      setCurtainUp(false);
    },

    drawOffer: () => backend.state.drawOffer ?? null,

    offerDraw() {
      if (backend.state.phase !== "playing") return;
      if (backend.state.drawOffer != null) return; // one offer at a time
      backend.dispatch(offerDrawAction(mySeat ?? viewer()));
    },

    acceptDraw() {
      if (backend.state.phase !== "playing") return;
      const offer = backend.state.drawOffer;
      if (offer == null) return;
      // The responder is the side that did NOT offer. Online, the worker
      // overrides this with the socket's seat anyway.
      backend.dispatch(acceptDrawAction(mySeat ?? otherPlayer(offer)));
      setSelection(null);
      setCurtainUp(false);
    },

    declineDraw() {
      if (backend.state.phase !== "playing") return;
      const offer = backend.state.drawOffer;
      if (offer == null) return;
      backend.dispatch(declineDrawAction(mySeat ?? otherPlayer(offer)));
    },

    undo() {
      backend.undo();
      setSelection(null);
      setCurtainUp(false);
    },

    reveal() {
      setCurtainUp(false);
    },

    setHideHands(value: boolean) {
      setSettings("hideHandsBetweenTurns", value);
    },
  };
}
